/**
 * Centralized HTTP client — without URL versioning.
 *
 * Replaces the former `apiRequest` from `src/services/apiService.ts`, removing
 * the `/vN/` prefix from requests. Keeps:
 *   - Refresh-token interceptor (401 → tries `/refreshToken` and retries)
 *   - File download support (responseType blob)
 *   - thirdPartyUrl support (bypasses the baseURL)
 *   - Same return shape: { response, error }
 *
 * Usage:
 *   import { http } from "@/api/client";
 *   const { response, error } = await http({ method: "GET", url: "getAllEvents" });
 *
 * Each file in `src/api/<domain>.ts` exports endpoint-typed functions that
 * wrap `http(...)` — this eases the inventory (every app call goes through
 * a single place) and eliminates the "scattering" of URLs across Redux actions.
 */
// @ts-nocheck
import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { store } from "../store/store";
import { logoutAction, refreshTokenAction } from "../store/actions/auth";
import { ALERT_DIALOG } from "../store/constants/auth";
// Base URL resolver: picks VITE_API_URL_LOCAL when in dev (if set).
import { API_BASE_URL as BASE_URL } from "./baseUrl";
import { isNotVerifiedError, showVerifyFirstDialog } from "../services/verifyFirst";

const controller = new AbortController();

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface HttpRequestInput {
  method: HttpMethod;
  url: string;
  data?: unknown;
  params?: Record<string, unknown>;
  isToken?: boolean;
  tempToken?: string | null;
  /** Extra request headers for endpoint-specific capabilities. */
  headers?: Record<string, string>;
  /** When true, a file download (blob) is expected. */
  file?: boolean;
  /** If set, ignores baseURL and hits this URL directly. */
  thirdPartyUrl?: string;
}

export interface HttpResult<T = any> {
  response: AxiosResponse<T> | null;
  error: any;
}

// Base URL resolved above (import.meta.env.VITE_API_URL_LOCAL in dev).

/** Refresh-token: uses the /refreshToken endpoint without a version prefix. */
const refreshAuth = async (): Promise<string | null> => {
  try {
    const { refreshToken } = store.getState().auth || {};
    if (!refreshToken) return null;
    const r = await axios.post(
      `${BASE_URL}/refreshToken`,
      { refreshToken },
      { signal: controller.signal },
    );
    const newToken = r?.data?.token;
    if (newToken) {
      store.dispatch(refreshTokenAction({ newToken }));
      return newToken;
    }
    return null;
  } catch (err: any) {
    if (err?.response?.status === 501) {
      store.dispatch(logoutAction({}));
      store.dispatch({
        type: ALERT_DIALOG,
        payload: {
          open: true,
          title: "Error!",
          message: "Your authentication session has expired. Please login again!",
        },
      });
    }
    return null;
  }
};

export async function http<T = any>(req: HttpRequestInput): Promise<HttpResult<T>> {
  const {
    method,
    url,
    data,
    params,
    isToken,
    tempToken = null,
    headers,
    file = false,
    thirdPartyUrl,
  } = req;

  let options: AxiosRequestConfig = {
    method,
    url: thirdPartyUrl || `${BASE_URL}/${url}`,
    headers: {
      Accept: "application/json",
      ...headers,
    },
    data,
    params: params || {},
    signal: controller.signal,
  };

  if (file) {
    options.responseType = "blob";
    options.headers = {
      ...options.headers,
      Accept:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    };
  }

  if (isToken) {
    const { auth } = store.getState();
    const token = tempToken || auth?.userToken;
    options.headers = {
      ...options.headers,
      authorization: `${token}`,
    };
  }

  try {
    const response = await axios(options);
    return { response, error: null };
  } catch (err: any) {
    if (err?.response?.status === 401 && isToken) {
      const newToken = await refreshAuth();
      if (newToken) {
        try {
          options.headers!.authorization = newToken;
          const response = await axios(options);
          return { response, error: null };
        } catch (err2: any) {
          return { response: null, error: err2 };
        }
      }
    }
    // Assigning someone who is not Verified: offer "Verify now" (onboarding, 2026-10-08).
    if (isNotVerifiedError(err)) showVerifyFirstDialog(err);
    return { response: null, error: err };
  }
}

/** Helper specific to multipart upload (FormData). */
export async function httpUpload<T = any>(
  url: string,
  formData: FormData,
  method: "POST" | "PUT" | "PATCH" = "PATCH",
): Promise<HttpResult<T>> {
  const { auth } = store.getState();
  try {
    const response = await axios({
      method,
      url: `${BASE_URL}/${url}`,
      headers: {
        Accept: "application/json",
        authorization: `${auth?.userToken ?? ""}`,
      },
      data: formData,
      signal: controller.signal,
    });
    return { response, error: null };
  } catch (err: any) {
    return { response: null, error: err };
  }
}

export const cancelAllRequests = () => {
  controller.abort();
};
